import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_FLAG = "lizta_chunk_reload";
const HARD_RELOAD_FLAG = "lizta_chunk_hard_reload";

async function hardReloadWithoutStaleCache() {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Ignore cleanup errors and continue with reload
  }

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Ignore cache cleanup errors and continue with reload
  }

  const current = new URL(window.location.href);
  current.searchParams.set("_lr", Date.now().toString());
  window.location.replace(current.toString());
}

/**
 * Evita tela em branco quando o usuário tem um bundle antigo em memória e o servidor
 * já publicou um deploy novo: o import dinâmico do chunk antigo falha (404).
 * Recarrega a página uma vez para buscar index.html e chunks alinhados.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const chunkFailed =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module") ||
        msg.includes("JavaScript-or-Wasm module script") ||
        msg.includes("text/html") ||
        msg.includes("Load failed");

      if (chunkFailed && !alreadyReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        window.location.reload();
      } else if (chunkFailed) {
        const alreadyHardReloaded = sessionStorage.getItem(HARD_RELOAD_FLAG) === "1";
        if (!alreadyHardReloaded) {
          sessionStorage.setItem(HARD_RELOAD_FLAG, "1");
          await hardReloadWithoutStaleCache();
        }
      }
      throw err;
    }
  });
}
